import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import apiService from '../services/api';
import AppShell, { useShellUser } from '../components/AppShell';
import { offerPath, wildfireStoreOffer } from '../utils/offerNavigation';
import useDragScroll from '../utils/useDragScroll';

const DEFAULT_REWARDS = {
  profile: 0,
  linkCard: 0,
  safariExtension: 0,
};

function normalizeRewards(data) {
  return {
    profile: Number(data?.profile ?? DEFAULT_REWARDS.profile) || 0,
    linkCard: Number(data?.link_card ?? data?.linkCard ?? DEFAULT_REWARDS.linkCard) || 0,
    safariExtension:
      Number(data?.safari_extension ?? data?.safariExtension ?? DEFAULT_REWARDS.safariExtension) || 0,
  };
}

function isHomecrowdActivity(entry) {
  if (!entry) return false;
  if (
    entry.activity_kind === 'setup_task_reward' ||
    entry.activity_kind === 'incentive_campaign' ||
    entry.activity_kind === 'homecrowd_bonus' ||
    entry.activity_kind === 'card_draw'
  ) {
    return true;
  }
  const metadata = entry.metadata || {};
  return (
    metadata.source === 'setup_task_reward' ||
    metadata.source === 'incentive_campaign' ||
    metadata.incentive_type === 'campaign_bonus'
  );
}

function formatPoints(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

function formatActivityDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function activityChannel(entry) {
  if (entry.source === 'wildfire') return 'Online';
  if (entry.source === 'olive') return 'In store';
  if (entry.source === 'travel') return 'Travel';
  if (isHomecrowdActivity(entry)) return 'HomeCrowd';
  return '';
}

function activityTitle(entry) {
  return entry.display_title || entry.merchant_name || entry.description || 'Activity';
}

function milestoneCost(milestone) {
  return Number(milestone?.pointsCost ?? milestone?.points_cost) || 0;
}

function scaleMinGap(leftText, rightText, rightIsLast) {
  const leftHalf = String(leftText).length * 4;
  const rightPart = rightIsLast ? String(rightText).length * 8 : String(rightText).length * 4;
  return ((leftHalf + rightPart + 8) / 420) * 100;
}

function ladderPositions(milestones) {
  const max = milestones.length ? milestoneCost(milestones[milestones.length - 1]) : 0;
  const ideals = milestones.map((item) => (max > 0 ? Math.min((milestoneCost(item) / max) * 100, 100) : 0));
  const n = ideals.length;
  if (!n) return [];
  const texts = milestones.map((item) => formatPoints(milestoneCost(item)));
  const gaps = [];
  for (let i = 0; i < n - 1; i += 1) {
    gaps.push(scaleMinGap(texts[i], texts[i + 1], i + 1 === n - 1));
  }
  let totalGap = gaps.reduce((sum, gap) => sum + gap, 0);
  if (totalGap > 100) {
    const shrink = 100 / totalGap;
    gaps.forEach((gap, index) => {
      gaps[index] = gap * shrink;
    });
  }
  const pos = ideals.slice();
  pos[n - 1] = 100;
  for (let pass = 0; pass < 40; pass += 1) {
    for (let i = 0; i < n - 1; i += 1) {
      const need = gaps[i];
      const cur = pos[i + 1] - pos[i];
      if (cur >= need) continue;
      const delta = need - cur;
      if (i + 1 === n - 1) pos[i] -= delta;
      else {
        pos[i] -= delta / 2;
        pos[i + 1] += delta / 2;
      }
    }
    pos[n - 1] = 100;
    for (let i = n - 2; i >= 0; i -= 1) pos[i] = Math.min(pos[i], pos[i + 1] - gaps[i]);
    pos[0] = Math.max(0, pos[0]);
    for (let i = 1; i < n; i += 1) pos[i] = Math.max(pos[i], pos[i - 1] + gaps[i - 1]);
    if (pos[n - 1] > 100) {
      const overflow = pos[n - 1] - 100;
      for (let i = 0; i < n; i += 1) pos[i] -= overflow;
      pos[n - 1] = 100;
    }
  }
  pos[n - 1] = 100;
  return pos.map((value) => Math.min(100, Math.max(0, value)));
}

function ladderFill(milestones, earned, positions) {
  const max = milestones.length ? milestoneCost(milestones[milestones.length - 1]) : 0;
  if (max <= 0 || earned <= 0) return 0;
  if (earned >= max) return 100;
  const costs = [0, ...milestones.map(milestoneCost)];
  const disp = [0, ...positions];
  for (let i = 1; i < costs.length; i += 1) {
    if (earned <= costs[i]) {
      const span = costs[i] - costs[i - 1];
      const t = span > 0 ? (earned - costs[i - 1]) / span : 1;
      return disp[i - 1] + (disp[i] - disp[i - 1]) * t;
    }
  }
  return 100;
}

function milestoneImage(milestone) {
  if (Array.isArray(milestone?.images) && milestone.images[0]) return milestone.images[0];
  return milestone?.imageUrl || milestone?.image_url || '';
}

function cardList(payload) {
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
}

function storeList(payload) {
  const rows = Array.isArray(payload?.click)
    ? payload.click
    : Array.isArray(payload?.results)
      ? payload.results
      : [];
  const seen = {};
  const stores = [];
  rows.forEach((item) => {
    if (!item || item.is_active === false) return;
    const logo = item.logoUrl || item.small_logo_url || item.large_logo_url || '';
    if (!logo) return;
    const id = item.id != null ? String(item.id) : item.name || item.merchantName;
    if (!id || seen[id]) return;
    seen[id] = true;
    stores.push({
      id,
      name: item.name || item.merchantName || 'Store',
      logo,
      offer: wildfireStoreOffer(item),
    });
  });
  return stores.slice(0, 8);
}

function HomePage() {
  const { user, setUser } = useShellUser();
  const storeDrag = useDragScroll();
  const [rewardPoints, setRewardPoints] = useState(DEFAULT_REWARDS);
  const [cardDone, setCardDone] = useState(false);
  const [safariDone, setSafariDone] = useState(false);
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [activity, setActivity] = useState([]);
  const [stores, setStores] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const applyUser = (nextUser) => {
      if (!nextUser) return;
      setUser(nextUser);
      const cardsDone =
        nextUser?.current_tier?.onboarding_status?.linked_card === true;
      const extensionDone =
        nextUser?.is_extension_enabled === true ||
        nextUser?.current_tier?.onboarding_status?.extension_installed === true;
      if (cardsDone) setCardDone(true);
      setSafariDone(extensionDone);
    };

    const applyFirstRewards = (payload, fallbackUser) => {
      const earned = Number(payload?.earnedPoints ?? payload?.earned_points);
      if (Number.isFinite(earned)) {
        setEarnedPoints(earned);
      } else {
        setEarnedPoints(
          Number(fallbackUser?.lifetime_points ?? fallbackUser?.current_points) || 0
        );
      }
      const rows = Array.isArray(payload?.milestones) ? payload.milestones.slice() : [];
      rows.sort((a, b) => milestoneCost(a) - milestoneCost(b));
      setMilestones(rows.slice(0, 3));
    };

    (async () => {
      try {
        const [freshUser, cardsData, rewards, activityLog, firstRewards, offers] =
          await Promise.all([
            apiService.fetchCurrentUser(),
            apiService.getPaymentCards().catch(() => ({ results: [] })),
            apiService.getSetupTaskRewards().catch(() => null),
            apiService.getUserActivityLog(12).catch(() => []),
            apiService.getFirstRewards().catch(() => null),
            apiService.getWildfireOffers({ page: 1, pageSize: 20 }).catch(() => null),
          ]);
        if (cancelled) return;

        applyUser(freshUser);
        const cards = cardList(cardsData);
        const linked =
          cards.some((card) => (card?.status || 'active') === 'active') ||
          freshUser?.current_tier?.onboarding_status?.linked_card === true;
        setCardDone(linked);
        setSafariDone(
          freshUser?.is_extension_enabled === true ||
            freshUser?.current_tier?.onboarding_status?.extension_installed === true
        );
        setRewardPoints(normalizeRewards(rewards));
        setActivity(Array.isArray(activityLog) ? activityLog : []);
        applyFirstRewards(firstRewards, freshUser);
        setStores(storeList(offers));

        try {
          const syncResult = await apiService.syncSetupTaskRewards();
          if (cancelled) return;
          if (syncResult?.rewards) setRewardPoints(normalizeRewards(syncResult.rewards));
          if (Number(syncResult?.total_points_awarded) > 0) {
            const [refreshedUser, refreshedActivity, refreshedRewards] = await Promise.all([
              apiService.fetchCurrentUser(),
              apiService.getUserActivityLog(12).catch(() => []),
              apiService.getFirstRewards().catch(() => null),
            ]);
            if (cancelled) return;
            applyUser(refreshedUser);
            setActivity(Array.isArray(refreshedActivity) ? refreshedActivity : []);
            applyFirstRewards(refreshedRewards, refreshedUser);
          }
        } catch (syncError) {
          void syncError;
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setUser]);

  const profileDone = user?.current_tier?.onboarding_status
    ? user.current_tier.onboarding_status.profile_created !== false
    : true;
  const doneCount = [profileDone, cardDone, safariDone].filter(Boolean).length;
  const firstName = String(user?.first_name || '').trim();
  const schoolLogo = user?.active_school?.image || '';

  const steps = [
    {
      key: 'profile',
      done: profileDone,
      title: profileDone ? 'Profile created' : 'Create your profile',
      description: 'Your profile is set.',
      points: rewardPoints.profile,
      cta: null,
    },
    {
      key: 'linkCard',
      done: cardDone,
      title: cardDone ? 'Card Earning activated' : 'Activate Card Earning',
      description: 'Takes about a minute. Your card number is never stored.',
      points: rewardPoints.linkCard,
      cta: 'Link my card',
    },
    {
      key: 'safari',
      done: safariDone,
      title: safariDone ? 'Safari Spending activated' : 'Activate Safari Spending',
      description: "Earn points when you shop online. We'll walk you through it.",
      points: rewardPoints.safariExtension,
      cta: 'Get started',
      href: '/extension-download/',
    },
  ];
  const currentStep = steps.find((step) => !step.done && step.cta);
  const setupComplete = !ready || doneCount === steps.length;

  const visibleMilestones = milestones.filter((item) => item && milestoneCost(item) > 0);
  const ladderMarks = ladderPositions(visibleMilestones);
  const ladderRatio = ladderFill(visibleMilestones, earnedPoints, ladderMarks) / 100;
  const nextLockedIndex = visibleMilestones.findIndex((item) => !item.unlocked);
  const nextReward = visibleMilestones.find((item) => !item.redeemed);

  const homecrowdRows = activity.filter(isHomecrowdActivity);
  const purchaseRows = activity.filter(
    (entry) => !isHomecrowdActivity(entry) && (entry.merchant_name || entry.source)
  );
  const activityRows = [...homecrowdRows, ...purchaseRows].slice(0, 4);

  return (
    <>
          <div className="home-welcome-row">
            <div>
              <h2>Welcome{firstName ? `, ${firstName}` : ''}!</h2>
              {!setupComplete ? <p>Finish setup to get closer to your first reward.</p> : null}
            </div>
            {!setupComplete ? (
            <div className="home-setup-meta">
              <div className="home-setup-label">
                <span>Setup</span>
                <span>
                  {doneCount} of 3 done
                </span>
              </div>
              <div className="home-setup-track" aria-hidden="true">
                {doneCount > 0 ? <span style={{ flex: doneCount }} /> : null}
                {doneCount < 3 ? <span className="is-active" style={{ flex: 0.7 }} /> : null}
                {doneCount < 3 ? (
                  <span className="is-rest" style={{ flex: Math.max(3 - doneCount, 0.2) }} />
                ) : null}
              </div>
            </div>
            ) : null}
          </div>

          {!setupComplete ? (
          <section className="home-steps" aria-label="Setup">
            {steps.map((step) => {
              const isCurrent = currentStep?.key === step.key;
              const body = step.done
                ? step.key === 'profile'
                  ? step.description
                  : null
                : step.description;
              return (
                <article
                  key={step.key}
                  className={`home-step${step.done ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}`}
                >
                  <div className="home-step-top">
                    {step.done ? (
                      <span className="home-step-check" aria-hidden="true">
                        <svg viewBox="0 0 16 16">
                          <path d="M3.2 8.2 6.4 11.4 12.8 4.6" />
                        </svg>
                      </span>
                    ) : (
                      <span className={`home-step-num${isCurrent ? ' is-current' : ''}`}>
                        {step.key === 'profile' ? 1 : step.key === 'linkCard' ? 2 : 3}
                      </span>
                    )}
                    <span className="home-step-points">+{formatPoints(step.points)} pts</span>
                  </div>
                  <h3>{step.title}</h3>
                  {body ? <p>{body}</p> : null}
                  {isCurrent && step.cta ? (
                    step.href ? (
                      <Link className="home-step-cta" to={step.href}>
                        {step.cta}
                      </Link>
                    ) : (
                      <button type="button" className="home-step-cta">
                        {step.cta}
                      </button>
                    )
                  ) : null}
                </article>
              );
            })}
          </section>
          ) : null}

          <div className="home-mid">
            <section className="home-earned" aria-label="Rewards progress">
              {schoolLogo ? (
                <img className="home-school-logo" src={schoolLogo} alt="" />
              ) : null}
              <p className="home-kicker">You've earned</p>
              <p className="home-earned-total">
                {formatPoints(earnedPoints)}
                <span>pts</span>
              </p>
              {visibleMilestones.length ? (
                <>
                  <div className="home-ladder">
                    <div className="home-ladder-track">
                      <span
                        className="home-ladder-fill"
                        style={{ width: `${Math.max(ladderRatio * 100, 4)}%` }}
                      />
                      {visibleMilestones.map((milestone, index) => (
                        <span
                          key={milestone.id || milestoneCost(milestone)}
                          className={`home-ladder-mark${milestone.unlocked ? ' is-reached' : ''}`}
                          style={{ left: `${ladderMarks[index] || 0}%` }}
                        />
                      ))}
                    </div>
                    <div className="home-ladder-labels">
                      {visibleMilestones.map((milestone, index) => (
                        <span
                          key={`label-${milestone.id || milestoneCost(milestone)}`}
                          style={{ left: `${ladderMarks[index] || 0}%` }}
                        >
                          {formatPoints(milestoneCost(milestone))}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="home-caption">Each goal you reach unlocks the next reward.</p>
                  <div className="home-rewards">
                    {visibleMilestones.map((milestone, index) => {
                      const image = milestoneImage(milestone);
                      const locked = !milestone.unlocked && index !== nextLockedIndex;
                      const isNext = nextReward && milestone.id === nextReward.id;
                      const cost = milestoneCost(milestone);
                      return (
                        <article key={milestone.id || cost} className="home-reward">
                          <div className="home-reward-photo">
                            {image ? <img src={image} alt="" /> : null}
                            {locked ? (
                              <span className="home-reward-lock" aria-hidden="true">
                                <svg viewBox="0 0 16 16">
                                  <rect x="3" y="7" width="10" height="7" rx="1.5" />
                                  <path d="M5.2 7V5.2a2.8 2.8 0 0 1 5.6 0V7" />
                                </svg>
                              </span>
                            ) : null}
                          </div>
                          <p className={`home-reward-next${isNext ? '' : ' is-spacer'}`}>
                            {isNext ? 'Next reward' : ''}
                          </p>
                          <h3>{milestone.title}</h3>
                          <p>
                            {isNext && !milestone.unlocked
                              ? `${formatPoints(earnedPoints)} / ${formatPoints(cost)} pts`
                              : `${formatPoints(cost)} pts`}
                          </p>
                          <button type="button" className="home-redeem" disabled>
                            {milestone.redeemed ? 'Redeemed' : 'Redeem'}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </>
              ) : ready ? (
                <p className="home-caption">Each goal you reach unlocks the next reward.</p>
              ) : null}
            </section>

            <section className="home-activity" aria-label="Recent activity">
              <div className="home-section-head">
                <h2>Recent activity</h2>
              </div>
              {activityRows.length ? (
                <ul className="home-activity-list">
                  {activityRows.map((entry) => {
                    const dateLabel = formatActivityDate(entry.date);
                    const channel = activityChannel(entry);
                    const points = Number(entry.points) || 0;
                    return (
                      <li key={entry.id}>
                        {schoolLogo ? (
                          <img className="home-activity-logo" src={schoolLogo} alt="" />
                        ) : (
                          <span className="home-activity-dot" aria-hidden="true" />
                        )}
                        <span className="home-activity-copy">
                          <span className="home-activity-title">{activityTitle(entry)}</span>
                          <span className="home-activity-meta">
                            {[dateLabel, channel].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className="home-activity-points">
                          {points > 0 ? `+${formatPoints(points)} pts` : `${formatPoints(points)} pts`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {purchaseRows.length === 0 ? (
                <div className="home-activity-empty">
                  <span className="home-activity-empty-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <rect x="6" y="3.5" width="12" height="17" rx="2" />
                      <path d="M9 8h6M9 12h6M9 16h3" />
                    </svg>
                  </span>
                  <p>Purchases show up here after you shop at a participating store.</p>
                </div>
              ) : null}
            </section>
          </div>

          <section className="home-stores" aria-label="Stores">
            <div className="home-section-head">
              <div className="home-stores-title">
                <h2>Earn where you already shop</h2>
                {cardDone ? null : (
                  <span className="home-card-badge">
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
                      <path d="M1.5 6.5h13" />
                    </svg>
                    Linked card required
                  </span>
                )}
              </div>
            </div>
            {stores.length ? (
              <ul className="home-store-row" {...storeDrag}>
                {stores.map((store) => (
                  <li key={store.id}>
                    <Link to={offerPath(store.offer)} state={{ offer: store.offer }} className="home-store-link">
                      <span className="home-store-tile">
                        <img src={store.logo} alt="" />
                      </span>
                      <span>{store.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : ready ? (
              <p className="home-stores-empty">Stores will show up here when offers are available.</p>
            ) : null}
          </section>
    </>
  );
}

function HomeGate() {
  if (!apiService.isAuthenticated()) return <Navigate to="/login" replace />;
  return (
    <AppShell title="Home">
      <HomePage />
    </AppShell>
  );
}

export default HomeGate;
